import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Alert,
  CircularProgress,
  Chip,
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  ClearAll as ClearAllIcon,
} from '@mui/icons-material';
import { fetchCouponUsages } from '../../store/slices/couponUsageSlice';
import AdminLayout from '../../components/layout/AdminLayout';
import { formatDate, formatCurrency } from '../../utils/formatters';
import { Link } from 'react-router-dom';

/**
 * Admin page for viewing coupon usage records
 * @returns {JSX.Element} The admin coupon usages page component
 */
const CouponUsagesPage = () => {
  const { t } = useTranslation('coupon');
  const dispatch = useDispatch();

  // Redux state
  const { usages, status, error, totalPages, currentPage, totalCount } = useSelector(
    (state) => state.couponUsages,
  );

  // Local state for filters
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [couponCodeFilter, setCouponCodeFilter] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [orderFilter, setOrderFilter] = useState('');

  // Load coupon usages on mount and when filters change
  useEffect(() => {
    const params = {
      page: page + 1,
      limit: rowsPerPage,
    };

    // Add filters if they exist
    if (couponCodeFilter) params.couponCode = couponCodeFilter;
    if (startDate) params.startDate = startDate.toISOString().split('T')[0];
    if (endDate) params.endDate = endDate.toISOString().split('T')[0];
    if (orderFilter) params.orderId = orderFilter;

    dispatch(fetchCouponUsages(params));
  }, [dispatch, page, rowsPerPage, couponCodeFilter, startDate, endDate, orderFilter]);

  // Handle pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle filter changes
  const handleCouponCodeFilterChange = (e) => {
    setCouponCodeFilter(e.target.value);
    setPage(0);
  };

  const handleStartDateChange = (date) => {
    setStartDate(date);
    setPage(0);
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
    setPage(0);
  };

  const handleOrderFilterChange = (e) => {
    setOrderFilter(e.target.value);
    setPage(0);
  };

  // Clear all filters
  const handleClearFilters = () => {
    setCouponCodeFilter('');
    setStartDate(null);
    setEndDate(null);
    setOrderFilter('');
    setPage(0);
  };

  // Derived data
  const loading = status === 'loading';

  return (
    <AdminLayout>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
          >
            {t('usages.title')}
          </Typography>
        </Box>

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <FilterIcon sx={{ mr: 1 }} />
            <Typography variant="h6">{t('filters.filters')}</Typography>
            <Button
              startIcon={<ClearAllIcon />}
              onClick={handleClearFilters}
              sx={{ ml: 'auto' }}
              size="small"
            >
              {t('filters.clearFilters')}
            </Button>
          </Box>

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid
              container
              spacing={2}
            >
              <Grid
                item
                xs={12}
                sm={6}
                md={3}
              >
                <TextField
                  fullWidth
                  label={t('usages.filters.couponCode')}
                  value={couponCodeFilter}
                  onChange={handleCouponCodeFilterChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid
                item
                xs={12}
                sm={6}
                md={3}
              >
                <TextField
                  fullWidth
                  label={t('usages.filters.orderNumber')}
                  value={orderFilter}
                  onChange={handleOrderFilterChange}
                />
              </Grid>
              <Grid
                item
                xs={12}
                sm={6}
                md={3}
              >
                <DatePicker
                  label={t('usages.filters.startDate')}
                  value={startDate}
                  onChange={handleStartDateChange}
                  renderInput={(props) => (
                    <TextField
                      {...props}
                      fullWidth
                    />
                  )}
                />
              </Grid>
              <Grid
                item
                xs={12}
                sm={6}
                md={3}
              >
                <DatePicker
                  label={t('usages.filters.endDate')}
                  value={endDate}
                  onChange={handleEndDateChange}
                  renderInput={(props) => (
                    <TextField
                      {...props}
                      fullWidth
                    />
                  )}
                />
              </Grid>
            </Grid>
          </LocalizationProvider>
        </Paper>

        {/* Error message */}
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 3 }}
          >
            {error}
          </Alert>
        )}

        {/* Coupon Usage Table */}
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: 640 }}>
            <Table
              stickyHeader
              aria-label="coupon usages table"
            >
              <TableHead>
                <TableRow>
                  <TableCell>{t('usages.table.couponCode')}</TableCell>
                  <TableCell>{t('usages.table.user')}</TableCell>
                  <TableCell>{t('usages.table.order')}</TableCell>
                  <TableCell>{t('usages.table.usedOn')}</TableCell>
                  <TableCell>{t('usages.table.orderStatus')}</TableCell>
                  <TableCell>{t('usages.table.orderValue')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      align="center"
                      sx={{ py: 3 }}
                    >
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : usages.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      align="center"
                      sx={{ py: 3 }}
                    >
                      {t('usages.table.noResults')}
                    </TableCell>
                  </TableRow>
                ) : (
                  usages.map((usage) => (
                    <TableRow key={usage.id}>
                      <TableCell>
                        <Typography fontWeight="bold">{usage.coupon?.code || 'N/A'}</Typography>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                        >
                          {usage.coupon?.type === 'percentage'
                            ? `${usage.coupon?.value}%`
                            : usage.coupon?.type === 'fixed_amount'
                            ? `${usage.coupon?.value} CHF`
                            : t('types.free_shipping')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography>{usage.user?.companyName || 'N/A'}</Typography>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                        >
                          {usage.user?.email || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {usage.order ? (
                          <Link to={`/admin/orders/${usage.order.id}`}>
                            {usage.order.orderNumber}
                          </Link>
                        ) : (
                          'N/A'
                        )}
                      </TableCell>
                      <TableCell>{formatDate(usage.usedAt)}</TableCell>
                      <TableCell>
                        {usage.order?.status ? (
                          <Chip
                            label={t(usage.order.status)}
                            color={
                              usage.order.status === 'processing' ||
                              usage.order.status === 'completed'
                                ? 'success'
                                : usage.order.status === 'pending'
                                ? 'warning'
                                : usage.order.status === 'canceled' ||
                                  usage.order.status === 'failed'
                                ? 'error'
                                : 'default'
                            }
                            size="small"
                          />
                        ) : (
                          'N/A'
                        )}
                      </TableCell>
                      <TableCell>
                        {usage.order?.total ? formatCurrency(usage.order.total, 'CHF') : 'N/A'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
          />
        </Paper>
      </Box>
    </AdminLayout>
  );
};

export default CouponUsagesPage;
